import { useTranslation } from 'react-i18next'

import { Header, SupplierForecastDrawerForm } from '@/components'
import { useParamsState } from '@/helpers'
import { AddCircleOutlineIcon, Button, Text } from '@mercai/clever'

import { SupplierForecastsTable, SupplierTabs } from '../../components'

import {
  DrawerShow,
  Metrics,
  SupplierForecastEditableTable,
} from './components'

import { QUERIES_ADMIN } from '@/services'
import { useQueryClient } from '@tanstack/react-query'
import styles from './tab-supplier-products.module.css'

export const TabSupplierProducts = () => {
  const { t } = useTranslation('screenSuppliers')

  const [params, setParams] = useParamsState({
    table: 'supplier-forecasts',
    newSupplierForecastForm: '',
    showDrawerShow: '',
  })

  const tabSelected = params?.table || 'supplier-forecasts'

  const queryClient = useQueryClient()

  const handleChangeTab = (
    tab: 'supplier-forecasts' | 'edit-supplier-forecasts',
  ) => {
    setParams({ ...params, table: tab })
  }

  const handleShowDrawerShow = (uuid: string) => {
    setParams({ ...params, showDrawerShow: uuid })
  }

  const onCompleteCreate = async () => {
    queryClient.invalidateQueries({
      queryKey: [QUERIES_ADMIN.BFF_FORECAST.module],
    })

    setParams({ ...params, newSupplierForecastForm: '' })
  }

  const contentObj: Record<
    string,
    {
      title: string | React.ReactNode
      description: string | React.ReactNode
      content: React.ReactNode
      actions: React.ReactNode
    }
  > = {
    'supplier-forecasts': {
      title: t('title'),
      description: t('description'),
      content: (
        <>
          <Metrics />
          <SupplierForecastsTable
            withSupplierColumn
            withTitle={false}
            withAddSupplierProduct={false}
            params={params}
            onShow={handleShowDrawerShow}
          />
        </>
      ),
      actions: (
        <div className={styles.actions}>
          <Button
            variant="secondary"
            model="pill"
            onClick={() => handleChangeTab('edit-supplier-forecasts')}
          >
            {t('editData')}
          </Button>
          <Button
            model="pill"
            onClick={() =>
              setParams({ ...params, newSupplierForecastForm: 'true' })
            }
          >
            {t('createAgreement')}
            <AddCircleOutlineIcon />
          </Button>
        </div>
      ),
    },
    'edit-supplier-forecasts': {
      title: (
        <div>
          <Text variant="title-large" className={styles.title}>
            {t('title')}
          </Text>
        </div>
      ),
      description: t('description'),
      content: <SupplierForecastEditableTable />,
      actions: (
        <div className={styles.actions}>
          <Button
            model="pill"
            variant="secondary"
            onClick={() =>
              setParams({ ...params, newSupplierForecastForm: 'true' })
            }
          >
            {t('createAgreement')}
          </Button>
          <Button
            model="pill"
            onClick={() => handleChangeTab('supplier-forecasts')}
          >
            {t('volverData')}
          </Button>
        </div>
      ),
    },
  }

  return (
    <>
      <div>
        <Header
          title={contentObj?.[tabSelected]?.title || ''}
          description={contentObj?.[tabSelected]?.description || ''}
          actions={contentObj?.[tabSelected]?.actions}
        />

        <SupplierTabs />

        <div className={styles.content}>
          {contentObj?.[tabSelected]?.content}
        </div>
      </div>

      <SupplierForecastDrawerForm
        isOpen={!!params.newSupplierForecastForm}
        onHandleOpen={() => {
          setParams({ ...params, newSupplierForecastForm: '' })
        }}
        onCompleteCreate={onCompleteCreate}
      />

      <DrawerShow
        isOpen={!!params.showDrawerShow}
        onClose={() => setParams({ ...params, showDrawerShow: '' })}
      />
    </>
  )
}
